<template>
  <ul :class="containerClass">
    <li v-if="firstLastButton" :class="[pageClass, firstPageSelected ? disabledClass : '']">
      <button
        type="button"
        :class="pageLinkClass"
        :disabled="firstPageSelected"
        :tabindex="firstPageSelected ? -1 : 0"
        @click="selectFirstPage()"
        @keyup.enter="selectFirstPage()"
        v-html="firstButtonText"
      />
    </li>

    <li
      v-if="!(firstPageSelected && hidePrevNext)"
      :class="[prevClass, firstPageSelected ? disabledClass : '']"
    >
      <button
        type="button"
        :disabled="firstPageSelected"
        :class="prevLinkClass"
        :tabindex="firstPageSelected ? -1 : 0"
        @click="prevPage()"
        @keyup.enter="prevPage()"
        v-html="prevText"
      />
    </li>

    <li
      v-for="page in pages"
      :key="page.index"
      :class="[
        pageClass,
        page.selected ? activeClass : '',
        page.disabled ? disabledClass : '',
        page.breakView ? breakViewClass : '',
      ]"
    >
      <button
        v-if="page.breakView"
        type="button"
        :class="[pageLinkClass, breakViewLinkClass]"
        tabindex="0"
        @click="clickBreakView(page.justifyPages)"
      >
        <slot name="breakViewContent">
          {{ breakViewText }}
        </slot>
      </button>
      <button
        v-else-if="page.disabled" type="button"
        :class="pageLinkClass"
        tabindex="0"
      >
        {{ page.content }}
      </button>
      <button
        v-else
        type="button"
        :class="pageLinkClass"
        tabindex="0"
        @click="handlePageSelected(page.index + 1)"
        @keyup.enter="handlePageSelected(page.index + 1)"
      >
        {{ page.content }}
      </button>
    </li>

    <li
      v-if="!(lastPageSelected && hidePrevNext)"
      :class="[nextClass, lastPageSelected ? disabledClass : '']"
    >
      <button
        type="button"
        :disabled="lastPageSelected"
        :class="nextLinkClass"
        :tabindex="lastPageSelected ? -1 : 0"
        @click="nextPage()"
        @keyup.enter="nextPage()"
        v-html="nextText"
      />
    </li>

    <li v-if="firstLastButton" :class="[pageClass, lastPageSelected ? disabledClass : '']">
      <button
        type="button"
        :disabled="lastPageSelected"
        :class="pageLinkClass"
        :tabindex="lastPageSelected ? -1 : 0"
        @click="selectLastPage()"
        @keyup.enter="selectLastPage()"
        v-html="lastButtonText"
      />
    </li>
    <li>{{ debugValue }}</li>
  </ul>
</template>

<script>
import { defineComponent } from 'vue';
import { EJustifyPages, PaginateHelper } from '../common/paginate-pages';

export default defineComponent({
  props: {
    modelValue: {
      type: Number,
    },
    pageCount: {
      type: Number,
      required: true,
    },
    forcePage: {
      type: Number,
    },
    clickHandler: {
      type: Function,
      default: () => {},
    },
    pageRange: {
      type: Number,
      default: 3,
    },
    marginPages: {
      type: Number,
      default: 1,
    },
    prevText: {
      type: String,
      default: 'Prev',
    },
    nextText: {
      type: String,
      default: 'Next',
    },
    breakViewText: {
      type: String,
      default: '…',
    },
    containerClass: {
      type: String,
    },
    pageClass: {
      type: String,
    },
    pageLinkClass: {
      type: String,
    },
    prevClass: {
      type: String,
    },
    prevLinkClass: {
      type: String,
    },
    nextClass: {
      type: String,
    },
    nextLinkClass: {
      type: String,
    },
    breakViewClass: {
      type: String,
    },
    breakViewLinkClass: {
      type: String,
    },
    activeClass: {
      type: String,
      default: 'active',
    },
    disabledClass: {
      type: String,
      default: 'disabled',
    },
    noLiSurround: {
      type: Boolean,
      default: false,
    },
    firstLastButton: {
      type: Boolean,
      default: false,
    },
    firstButtonText: {
      type: String,
      default: 'First',
    },
    lastButtonText: {
      type: String,
      default: 'Last',
    },
    hidePrevNext: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      innerValue: 1,
      justifyPages: EJustifyPages.ALL_0,
      pagesBefore: 2,
      pagesAfter: 2,
      debugValue: null,
    };
  },
  computed: {
    selected: {
      get() {
        return this.modelValue || this.innerValue;
      },
      set(newValue) {
        this.innerValue = newValue;
      },
    },
    pages() {
      const helper = new PaginateHelper(this);

      return helper.getPagesArray();
    },
    firstPageSelected() {
      return this.selected === 1;
    },
    lastPageSelected() {
      return this.selected === this.pageCount || this.pageCount === 0;
    },
  },
  beforeUpdate() {
    if (this.forcePage === undefined) return;
    if (this.forcePage !== this.selected) {
      this.selected = this.forcePage;
    }
  },
  methods: {
    handlePageSelected(selected) {
      if (this.selected === selected) return;

      this.innerValue = selected;
      this.$emit('update:modelValue', selected);
      this.clickHandler(selected);
    },
    prevPage() {
      if (this.selected <= 1) return;

      this.handlePageSelected(this.selected - 1);
    },
    nextPage() {
      if (this.selected >= this.pageCount) return;

      this.handlePageSelected(this.selected + 1);
    },
    selectFirstPage() {
      if (this.selected <= 1) return;

      this.handlePageSelected(1);
    },
    selectLastPage() {
      if (this.selected >= this.pageCount) return;

      this.handlePageSelected(this.pageCount);
    },
    clickBreakView(justifyPages) {
      if (this.justifyPages === justifyPages) {
        this.justifyPages = EJustifyPages.ALL_0;
      } else {
        this.justifyPages = justifyPages;
      }
    },
  },
});
</script>
